import * as NextSkyTheme from "@NextSkyTheme/global";
import { ProductForm } from "@NextSkyTheme/add-to-cart";
import { LazyLoadEventHover, LazyLoader } from "@NextSkyTheme/lazy-load";
import { notifier } from "@NextSkyTheme/notification";
import { eventModal } from "@NextSkyTheme/modal";

LazyLoadEventHover.run();

function getQuantityInputMax(input) {
  if (!input) return null;
  if (input.hasAttribute("data-inventory-max")) {
    const inventoryMax = parseInt(input.getAttribute("data-inventory-max"), 10);
    if (!Number.isNaN(inventoryMax)) return inventoryMax;
  }
  if (input.hasAttribute("max")) {
    const max = parseInt(input.max, 10);
    if (!Number.isNaN(max)) return max;
  }
  return null;
}

function syncQuantityInputFromSource(sourceInput, targetInput) {
  if (!sourceInput || !targetInput) return;

  ["min", "step"].forEach((attr) => {
    if (sourceInput.hasAttribute(attr)) {
      targetInput.setAttribute(attr, sourceInput.getAttribute(attr));
    } else {
      targetInput.removeAttribute(attr);
    }
  });

  if (sourceInput.hasAttribute("data-inventory-max")) {
    targetInput.setAttribute(
      "data-inventory-max",
      sourceInput.getAttribute("data-inventory-max"),
    );
  } else {
    targetInput.removeAttribute("data-inventory-max");
  }

  const sourceMax = sourceInput.getAttribute("max");
  if (sourceMax && parseInt(sourceMax, 10) > 0) {
    targetInput.setAttribute("max", sourceMax);
  } else {
    targetInput.removeAttribute("max");
  }

  const min = targetInput.min ? parseInt(targetInput.min, 10) : 1;
  const max = getQuantityInputMax(targetInput);
  let value = parseInt(targetInput.value, 10) || min;
  if (max !== null && max > 0 && value > max) value = max;
  if (value < 1) value = 1;
  targetInput.value = value;
  targetInput.closest("quantity-input")?.validateQtyRules?.();
}

function buildEditCartRefreshUrl(modal) {
  const variantInput = modal.querySelector("variant-input");
  const form = modal.querySelector("product-form-edit-cart form");
  const productUrl =
    variantInput?.getAttribute("data-product-url") ||
    modal.getAttribute("data-product-url");
  const variantId = form?.querySelector('[name="id"]')?.value;
  if (!productUrl || !variantId) return null;

  const url = new URL(productUrl, window.shopUrl);
  url.searchParams.set("section_id", "main-cart-edit");
  url.searchParams.set("variant", variantId);

  const editLineQty = modal.getAttribute("data-edit-line-quantity");
  const editVariantId = modal.getAttribute("data-edit-variant-id");
  if (editLineQty) url.searchParams.set("edit_line_quantity", editLineQty);
  if (editVariantId) url.searchParams.set("edit_variant_id", editVariantId);

  return url.toString();
}

function refreshEditCartQuantityLimits(modal) {
  if (!modal?.classList.contains("active")) return Promise.resolve();

  const refreshUrl = buildEditCartRefreshUrl(modal);
  if (!refreshUrl) return Promise.resolve();

  return fetch(refreshUrl)
    .then((response) => response.text())
    .then((html) => {
      const parsed = NextSkyTheme.parser.parseFromString(html, "text/html");
      const section =
        parsed.getElementById("shopify-section-main-cart-edit") || parsed;
      const sourceQty = section.querySelector('input[name="quantity"]');
      const targetQty = modal.querySelector('input[name="quantity"]');
      syncQuantityInputFromSource(sourceQty, targetQty);

      const sourceSubmit = section.querySelector('[type="submit"]');
      const targetSubmit = modal.querySelector('[type="submit"]');
      if (sourceSubmit && targetSubmit) {
        if (sourceSubmit.disabled) {
          targetSubmit.setAttribute("disabled", "disabled");
        } else {
          targetSubmit.removeAttribute("disabled");
        }
      }
    })
    .catch((error) => console.error(error));
}

class MainCartEdit extends HTMLElement {
  constructor() {
    super();
    this.init();
    this.key = this.getAttribute("data-key");
    this.quantity = this.getAttribute("data-quantity");
    this.index = this.getAttribute("data-index");
    this.variantId = this.getAttribute("data-id");
    const href = this.getAttribute("data-href");
    const urlLocal = window.shopUrl;
    const variant =
      href.indexOf("?") > -1 ||
      href.indexOf("?variant=") > -1 ||
      href.indexOf("&variant=") > -1
        ? "&"
        : "/?";
    this.url =
      urlLocal +
      href +
      variant +
      "section_id=main-cart-edit&edit_line_quantity=" +
      encodeURIComponent(this.quantity) +
      "&edit_variant_id=" +
      encodeURIComponent(this.variantId);
  }

  init() {
    this.addEventListener("click", (e) => this.onClick(e));
    this.addEventListener("keyup", (e) => {
      if (e.key === "Enter") {
        this.onClick(e);
      }
    });
  }

  onClick(e) {
    this.addLoading(true);
    e.preventDefault();
    fetch(this.url)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          throw error;
        }
        return response.text();
      })
      .then((response) => {
        const resultsHtml = NextSkyTheme.parser
          .parseFromString(response, "text/html")
          .getElementById("shopify-section-main-cart-edit");
        const modal = resultsHtml.querySelector("main-cart-edit-popup");
        NextSkyTheme.getBody().appendChild(modal);
        new LazyLoader(".image-lazy-load");
        setTimeout(
          () =>
            eventModal(
              document.querySelector("main-cart-edit-popup"),
              "open",
              true
            ),
          100
        );
        NextSkyTheme.global.rootToFocus = this;
      })
      .catch((error) => {
        throw error;
      })
      .finally(() => {
        this.addLoading(false);
        const modal = document.querySelector("main-cart-edit-popup");
        if (!modal) return;
        const quantityInput = modal.querySelector(
          'quantity-input [name="quantity"]'
        );
        if (quantityInput) {
          quantityInput.value = this.quantity;
          quantityInput
            .closest("quantity-input")
            ?.validateQtyRules?.();
        }
        const elementTotalPrice = modal.querySelector(".total-price__detail");
        if (elementTotalPrice) {
          const dataTotalPrice =
            elementTotalPrice.getAttribute("data-total-price");
          const totalPrice = NextSkyTheme.formatMoney(
            dataTotalPrice * this.quantity,
            cartStrings.money_format
          );
          elementTotalPrice.textContent = totalPrice;
        }
        modal.setAttribute("data-key", this.key);
        modal.setAttribute("data-index", this.index);
        modal.setAttribute("data-edit-line-quantity", this.quantity);
        modal.setAttribute("data-edit-variant-id", this.variantId);
        modal.setAttribute("data-product-url", this.getAttribute("data-href"));
      });
  }

  addLoading(action = false) {
    if (action) {
      this.classList.add("loading");
    } else {
      this.classList.remove("loading");
    }
  }
}
customElements.define("main-cart-edit", MainCartEdit);

if (!customElements.get("product-form-edit-cart")) {
  customElements.define(
    "product-form-edit-cart",
    class ProductFormCartEdit extends ProductForm {
      constructor() {
        super();
        this.form = this.querySelector("form");
        this.cart = document.querySelector("main-cart");
        if (this.form) {
          if (this.form.querySelector("[name=id]")) {
            this.form.querySelector("[name=id]").disabled = false;
          }
          this.form.addEventListener("submit", this.onSubmitHandler.bind(this));

          this.submitButton = this.querySelector('[type="submit"]');
          if (this.cart && this.submitButton)
            this.submitButton.setAttribute("aria-haspopup", "dialog");
          this.hideErrors = this.dataset.hideErrors === "true";
        }
        this.modal = document.querySelector("main-cart-edit-popup");
        this.cartUpdateUnsubscriber = undefined;
        this.quantityUpdateUnsubscriber = undefined;
      }

      connectedCallback() {
        this.cartUpdateUnsubscriber = NextSkyTheme.subscribe(
          NextSkyTheme.PUB_SUB_EVENTS.cartUpdate,
          () => {
            if (this.modal?.classList.contains("active")) {
              refreshEditCartQuantityLimits(this.modal);
            }
          }
        );
        this.quantityUpdateUnsubscriber = NextSkyTheme.subscribe(
          NextSkyTheme.PUB_SUB_EVENTS.quantityUpdate,
          () => {
            if (this.modal?.classList.contains("active")) {
              this.quantityInput
                ?.closest("quantity-input")
                ?.validateQtyRules?.();
            }
          }
        );
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
        if (this.quantityUpdateUnsubscriber) {
          this.quantityUpdateUnsubscriber();
        }
      }

      get cartKey() {
        return this.modal?.getAttribute("data-key");
      }

      get cartIndex() {
        return this.modal?.getAttribute("data-index");
      }

      get sectionId() {
        return this.cart.getAttribute("data-section-id");
      }

      get quantityInput() {
        return this.form?.querySelector('[name="quantity"]');
      }

      validateQuantityBeforeSubmit() {
        const input = this.quantityInput;
        if (!input) return true;

        const requestedQty = parseInt(input.value, 10) || 1;
        const min = input.min ? parseInt(input.min, 10) : 1;
        const max = getQuantityInputMax(input);

        if (max !== null && max === 0) {
          this.handleErrorMessage(window.cartStrings.sold_out);
          notifier.show(window.cartStrings.sold_out, "warning", 4000);
          return false;
        }

        if (max !== null && max > 0 && requestedQty > max) {
          input.value = max;
          input.closest("quantity-input")?.validateQtyRules?.();
          const message = (window.cartStrings?.quantityError || "").replace(
            "[quantity]",
            max
          );
          this.handleErrorMessage(message);
          notifier.show(message, "warning", 4000);
          return false;
        }

        if (requestedQty < min) {
          input.value = min;
          input.closest("quantity-input")?.validateQtyRules?.();
          return false;
        }

        return true;
      }

      onSubmitHandler(event) {
        event.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;
        this.handleErrorMessage();
        if (!this.validateQuantityBeforeSubmit()) return;

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");

        this.removeCartLine()
          .then(() => this.addNewCart())
          .catch((error) => {
            console.error(error);
            notifier.show(
              error.message || window.cartStrings.error,
              "error",
              4000
            );
            this.resetSubmitButton();
          });
      }

      removeCartLine() {
        const cartKey = this.cartKey;
        if (!cartKey) {
          return Promise.reject(new Error(window.cartStrings.error));
        }

        const body = JSON.stringify({
          id: cartKey,
          quantity: 0,
          sections: this.getSectionsToRender().map(
            (section) => section.section || section.id
          ),
          sections_url: window.location.pathname,
        });

        return fetch(`${routes?.cart_change_url}`, {
          ...NextSkyTheme.fetchConfig(),
          body,
        })
          .then((response) => response.text())
          .then((state) => {
            const parsedState = JSON.parse(state);
            if (parsedState.status || parsedState.errors) {
              throw new Error(
                parsedState.description ||
                  parsedState.errors ||
                  window.cartStrings.error
              );
            }
            return parsedState;
          });
      }

      isPartialCartUpdate(response) {
        if (!response?.status) return false;
        if (response.sections) return true;

        const description = (response.description || "").toLowerCase();
        return (
          description.includes("only available") ||
          description.includes("available quantity")
        );
      }

      handleCartAddResponse(response) {
        if (this.isPartialCartUpdate(response)) {
          this.updateMainCart(response);
          this.closeEditModal();
          notifier.show(response.description, "warning", 4000);
          this.resetSubmitButton(false);
          return;
        }

        if (response.status) {
          if (response.sections) {
            this.updateMainCart(response);
            this.closeEditModal();
          }
          this.handleErrorMessage(response.description);
          notifier.show(response.description, "error", 4000);
          this.resetSubmitButton(!response.sections);
          return;
        }

        this.updateMainCart(response);
        this.closeEditModal();
        notifier.showElement(
          window.cartStrings.updateCartSuccess,
          null,
          "success",
          2500
        );
        this.resetSubmitButton(false);
      }

      closeEditModal() {
        if (!this.modal) return;
        eventModal(
          document.querySelector("main-cart-edit-popup"),
          "close",
          true
        );
      }

      resetSubmitButton(reEnable = true) {
        this.addLoading(this.cartIndex, false);
        new NextSkyTheme.FSProgressBar("free-ship-progress-bar");
        this.submitButton.classList.remove("loading");
        if (reEnable) {
          this.submitButton.removeAttribute("aria-disabled");
        }
      }

      addNewCart() {
        this.addLoading(this.cartIndex, true);
        const config = NextSkyTheme.fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];
        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            "sections",
            this.getSectionsToRender().map((section) => section.id)
          );
          formData.append("sections_url", window.location.pathname);
          NextSkyTheme.global.rootToFocus = this.submitButton;
        }
        config.body = formData;
        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            this.handleCartAddResponse(response);
          })
          .catch((e) => {
            console.error(e);
            notifier.show(window.cartStrings.error, "error", 4000);
            this.resetSubmitButton();
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;
        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );
        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);
        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      getSectionsToRender() {
        return [
          {
            id: this.sectionId,
            section: this.sectionId,
            selector: ".main-cart-items",
          },
          {
            id: "cart-icon-bubble",
            section: "cart-icon-bubble",
          },
          {
            id: this.sectionId,
            section: this.sectionId,
            selector: ".free-shipping-bar",
          },
          {
            id: this.sectionId,
            section: this.sectionId,
            selector: ".cart-info .totals",
          },
          {
            id: this.sectionId,
            section: this.sectionId,
            selector: ".cart-recommendations",
          },
        ];
      }
    }
  );
}
